import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Concentration, DEFAULT_LEGACY_MEASUREMENT, LegacyMeasurement} from 'constants/MeasurementConstants';
import {
  ContainableSummary,
  ModalPane,
  SelectedParentStorable,
  ValidationErrors,
} from 'constants/SampleConstants';
import {
  SampleControlAttributes,
  SampleControlFields,
  SampleRestrictionStatus,
  SampleRoleParty,
} from 'constants/SampleStatusConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import SampleControlFieldsSection from 'inventory/sample-status/SampleControlFieldsSection';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {queryTagSchemaHasContainerNameTemplate} from 'name-templates/ContainerNameTemplateUtil';
import NameTemplateInfoBanner from 'name-templates/NameTemplateInfoBanner';
import ContainableSearchInput from 'samples/containables/ContainableSearchInput';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {isBioentitySummary} from 'samples/StorableModalsUtil';
import {ContainerSchema, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {NumericInputChangeEvent} from 'util/Types';

import BaseStorablePane, {CreateStorablePaneState, DEFAULT_CREATE_STORABLE_STATE} from '../BaseStorablePane';
import BaseStorablePaneStudyableFormGroup, {
  BaseStorablePaneStudyableProps,
  BaseStorablePaneStudyableState,
} from '../BaseStorablePaneStudyableFormGroup';
import ContainerSchemaSelector from '../ContainerSchemaSelector';
import {MeasurementType, NewMeasurementForm} from '../MeasurementForm';

export interface CreateContainerPaneProps extends SampleControlAttributes, BaseStorablePaneStudyableProps {
  concentration: LegacyMeasurement;
  customBarcode: string;
  disallowContainableEdit: boolean;
  willApplyNameTemplate: boolean;
  inputContainable: ContainableSummary | null;
  name: string;
  numToCreate: number;
  numRegistries?: number;
  onUpdate: (props: Partial<CreateContainersPaneState>) => void;
  quantity: LegacyMeasurement;
  registryId?: string;
  selectedParent?: SelectedParentStorable;
  selectedSchema: ContainerSchema | null;
  shouldAutogenerateBarcode: boolean;
  shouldShowPlaceholderContainable?: boolean;
  sourceId?: string;
  tags: Array<SchemaTag>;
  validationErrors: ValidationErrors;
}

export interface CreateContainersPaneState
  extends CreateStorablePaneState, SampleControlAttributes, BaseStorablePaneStudyableState {
  selectedSchema: ContainerSchema | null;
  concentration: Concentration;
  gridCoordinates: {columnIndex: number | null; rowIndex: number | null};
  inputContainable: ContainableSummary | null;
  willApplyNameTemplate: boolean;
  quantity: LegacyMeasurement;
}

export const DEFAULT_CREATE_CONTAINERS_PANE_STATE: CreateContainersPaneState = {
  ...DEFAULT_CREATE_STORABLE_STATE,
  concentration: DEFAULT_LEGACY_MEASUREMENT,
  gridCoordinates: {columnIndex: null, rowIndex: null},
  inputContainable: null,
  willApplyNameTemplate: false,
  registry: null,
  quantity: DEFAULT_LEGACY_MEASUREMENT,
  sampleRoles: {sampleOwners: [], restrictedSampleUsers: []},
  restrictionStatus: SampleRestrictionStatus.UNRESTRICTED,
};

class CreateContainerPane extends React.Component<CreateContainerPaneProps> {
  static defaultProps = {
    shouldShowPlaceholderContainable: false,
  };

  private handleChangeContainerSchema = (selectedContainerSchema: StorableSchema | null): void => {
    this.props.onUpdate({
      selectedSchema: selectedContainerSchema,
      tags: selectedContainerSchema ? TagUtil.getInitialTags(selectedContainerSchema.fields) : [],
    });
  };

  private handleChangeContainable = async (containables: Array<ContainableSummary>): Promise<void> => {
    const inputContainable = containables.length ? containables[0] : null;
    const willApplyNameTemplate =
      isBioentitySummary(inputContainable) &&
      inputContainable.tagSchema != null &&
      (await queryTagSchemaHasContainerNameTemplate(inputContainable.tagSchema.id));
    this.props.onUpdate({inputContainable, willApplyNameTemplate});
  };

  private handleChangeConcentrationUnits = ({val: units}: {val: string | null}): void => {
    this.props.onUpdate({
      concentration: {...this.props.concentration, units},
    });
  };

  private handleChangeConcentrationValue = (e: NumericInputChangeEvent): void => {
    this.props.onUpdate({
      concentration: {...this.props.concentration, value: e.target.numericValue},
    });
  };

  private handleChangeQuantityUnits = ({val: units}: {val: string | null}): void => {
    this.props.onUpdate({
      quantity: {...this.props.quantity, units},
    });
  };

  private handleChangeQuantityValue = (e: NumericInputChangeEvent): void => {
    this.props.onUpdate({
      quantity: {...this.props.quantity, value: e.target.numericValue},
    });
  };

  private handleUpdateRestrictionStatus = (restrictionStatus: SampleRestrictionStatus): void => {
    this.props.onUpdate({restrictionStatus});
  };

  private handleUpdateSampleOwners = (sampleOwners: Array<SampleRoleParty>): void => {
    this.props.onUpdate({sampleRoles: {...this.props.sampleRoles, sampleOwners}});
  };

  private handleUpdateRestrictedSampleUsers = (restrictedSampleUsers: Array<SampleRoleParty>): void => {
    this.props.onUpdate({sampleRoles: {...this.props.sampleRoles, restrictedSampleUsers}});
  };

  private handleUpdateSampleControls = (controls: Partial<SampleControlFields>): void => {
    if (controls.restrictionStatus != null) {
      this.handleUpdateRestrictionStatus(controls.restrictionStatus);
    }
    if (controls.sampleOwners != null) {
      this.handleUpdateSampleOwners(controls.sampleOwners);
    }
    if (controls.restrictedSampleUsers != null) {
      this.handleUpdateRestrictedSampleUsers(controls.restrictedSampleUsers);
    }
  };

  private renderSchemaSelector = (): JSX.Element => (
    <ContainerSchemaSelector
      onChange={this.handleChangeContainerSchema}
      registryId={this.props.registryId}
      selectedContainerSchemaId={this.props.selectedSchema ? this.props.selectedSchema.id : null}
      shouldShowNoSchemaOption={false}
      error={this.props.validationErrors.schemaError}
    />
  );

  override render(): JSX.Element {
    const sharedQuantityProps = {
      error: this.props.validationErrors.quantityError,
      measurement: this.props.quantity,
      onChangeUnits: this.handleChangeQuantityUnits,
      onChangeValue: this.handleChangeQuantityValue,
    };
    const sharedConcentrationProps = {
      error: this.props.validationErrors.concentrationError,
      measurement: this.props.concentration,
      onChangeUnits: this.handleChangeConcentrationUnits,
      onChangeValue: this.handleChangeConcentrationValue,
    };
    return (
      <BaseStorablePane
        canCreateMultiple={true}
        nameLabel="Container name"
        modalPane={ModalPane.CREATE_CONTAINERS}
        schemaSelector={this.renderSchemaSelector()}
        {...this.props}
        selectedSchema={this.props.selectedSchema ?? undefined}
      >
        <FormGroup>
          <FieldLabel label="Contents" labelStyle={FieldLabel.STYLES.LABEL} />
          {this.props.shouldShowPlaceholderContainable ? (
            <div
              className="u-italic legacy-font-proxima-13 u-tiny-spacing-top"
              {...getTestProps({component: 'CreateContainerPane', element: 'placeholderContainable'})}
            >
              New entity or batch
            </div>
          ) : (
            <ContainableSearchInput
              autoFocus={false}
              error={this.props.validationErrors.contentsError}
              onChange={this.handleChangeContainable}
              isReadOnly={this.props.disallowContainableEdit}
              selectedContainables={this.props.inputContainable ? [this.props.inputContainable] : []}
            />
          )}
        </FormGroup>
        {this.props.willApplyNameTemplate && (
          <div className={css(styles.infoBanner)}>
            <NameTemplateInfoBanner
              itemDescription="container"
              isPlural={this.props.numToCreate > 1}
              nameTemplateAppliesToAllItems={true}
            />
          </div>
        )}
        {this.props.inputContainable && (
          <div className={css(styles.row)}>
            <FormGroup className={css(styles.column)}>
              <FieldLabel label={getQuantityDisplayString()} labelStyle={FieldLabel.STYLES.LABEL} />
              <NewMeasurementForm {...sharedQuantityProps} measurementType={MeasurementType.QUANTITY} />
            </FormGroup>
            <FormGroup className={css(styles.column)}>
              <FieldLabel label="Concentration" labelStyle={FieldLabel.STYLES.LABEL} />
              <NewMeasurementForm
                {...sharedConcentrationProps}
                measurementType={MeasurementType.CONCENTRATION}
              />
            </FormGroup>
          </div>
        )}
        {this.props.inputContainable && hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3) && (
          <SampleControlFieldsSection
            restrictionStatus={this.props.restrictionStatus}
            restrictedSampleUsers={this.props.sampleRoles.restrictedSampleUsers}
            sampleOwners={this.props.sampleRoles.sampleOwners}
            onChangeSampleControls={this.handleUpdateSampleControls}
          />
        )}
        <BaseStorablePaneStudyableFormGroup
          studySearchInputProps={this.props.studySearchInputProps}
          studyIds={this.props.studyIds}
          onUpdate={this.props.onUpdate}
        />
      </BaseStorablePane>
    );
  }
}

const styles = StyleSheet.create({
  infoBanner: {
    marginBottom: 15,
  },
  row: {
    display: 'flex',
    gap: 24,
  },
  column: {
    flex: 1,
  },
});

export default CreateContainerPane;
